import {
  AxiosError,
  AxiosHeaders,
  type AxiosAdapter,
  type InternalAxiosRequestConfig,
} from 'axios';
import i18n from '@/i18n';
import { apiClient } from '@/api/client';
import type { DemoLanguage, DemoRoute, TourDefinition } from './types';

/**
 * Serves a tour's demo records from inside the app's own axios client.
 *
 * A request interceptor looks at each outgoing request and, when the tour
 * owns its path, swaps in an adapter that answers locally. Nothing else
 * changes: the response still runs through every response interceptor, and
 * the page's hooks, types and query keys are the ones it uses every day. That
 * is the point of answering at this layer. The page cannot tell, so the tour
 * shows the real screen, not a mock-up of it.
 *
 * Returns the function that removes the interceptor.
 */
export function installDemoApi(
  definition: TourDefinition,
  lang: DemoLanguage,
): () => void {
  const id = apiClient.interceptors.request.use((config) => {
    const { path, params } = describe(config);
    const method = (config.method ?? 'get').toLowerCase();

    const request = { path, params, lang };
    const route = definition.demo.find(
      (r) => matches(r, method, path) && (!r.when || r.when(request)),
    );
    if (route) {
      config.adapter = answer(route.respond(request));
    } else if (
      method !== 'get' &&
      definition.writeGuard.some((prefix) => path.startsWith(prefix))
    ) {
      config.adapter = refuse;
    }
    return config;
  });

  return () => apiClient.interceptors.request.eject(id);
}

function matches(route: DemoRoute, method: string, path: string) {
  if ((route.method ?? 'get') !== method) return false;
  return typeof route.path === 'string'
    ? route.path === path
    : route.path.test(path);
}

/** The path relative to the API root, and every query parameter as a string. */
function describe(config: InternalAxiosRequestConfig) {
  const url = new URL(config.url ?? '', 'http://demo.invalid');
  const params: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  Object.entries((config.params ?? {}) as Record<string, unknown>).forEach(
    ([key, value]) => {
      if (value !== undefined && value !== null) params[key] = String(value);
    },
  );
  return { path: url.pathname.replace(/\/+$/, '') || '/', params };
}

function answer(data: unknown): AxiosAdapter {
  return async (config) => ({
    data,
    status: 200,
    statusText: 'OK',
    headers: new AxiosHeaders(),
    config,
    request: {},
  });
}

/**
 * A write against demo records. Shaped like the API's own refusals (message
 * at the top, code under `errors`), so whatever toast the page raises for a
 * failed save says the right thing.
 */
const refuse: AxiosAdapter = async (config) => {
  const message = i18n.t('tour.demoWriteRefused');
  throw new AxiosError(message, 'ERR_TOUR_DEMO', config, undefined, {
    data: { message, errors: { code: 'TOUR_DEMO' } },
    status: 409,
    statusText: 'Conflict',
    headers: new AxiosHeaders(),
    config,
  });
};
